import { z } from "zod"
import { err, ok, type Result } from "./result"

/** One carrier-price band. Order is significant: the first matching bound wins. */
export const shippingTierSchema = z.union([
  z.object({ upToKg: z.number().positive(), price: z.number().nonnegative() }),
  z.object({ underKg: z.number().positive(), price: z.number().nonnegative() }),
  z.object({ perKg: z.number().nonnegative() }),
])
export type ShippingTier = z.infer<typeof shippingTierSchema>

/** Shared on-disk fee block used by both API and CLI config loaders. */
export const marketplaceFeeConfigSchema = z.object({
  feeModelVersion: z.string().trim().min(1),
  currency: z.string().trim().min(1),
  commissionPct: z.number().min(0).max(1, "commissionPct is a fraction, e.g. 0.16 for 16%"),
  commissionIncludesShipping: z.boolean(),
  customerShippingPrice: z.number().nonnegative(),
  shippingTiers: z.array(shippingTierSchema).min(1),
  monthlyFixedFee: z.number().nonnegative(),
  // Units of the marketplace's ORDER currency per 1 unit of `currency` — set only
  // when a marketplace TRADES in one currency and is REPORTED in another.
  //
  // `currency` is what the book is stated in, not what the marketplace charges.
  // Decathlon CZ and PL trade in CZK and PLN but are reported in EUR, so that
  // every Decathlon market can be read side by side without the reader doing
  // arithmetic. The order-sourced figures (revenue and realised commission) are
  // the only ones that arrive in the trading currency; catalog cost is already
  // EUR on every store, and shipping tiers, customer shipping and the monthly fee
  // are declared in `currency` directly. So this rate divides exactly two inputs
  // and nothing else — see `computeOrdersPnl`.
  //
  // Absent = the marketplace is reported in the currency it trades in (every
  // Shop Apotheke market, and Decathlon IT).
  //
  // A DECLARED rate, not a live lookup, for the same reason `monthlyFixedFee`
  // carries a converted number: a persisted book must be reproducible, and a
  // figure that silently moves with the FX market cannot be reconciled against
  // what was reported last month. The cost is that history is priced at one
  // rate; re-check it on a `feeModelVersion` bump.
  orderCurrencyRate: z.number().positive().optional(),
})
export type MarketplaceFeeConfig = z.infer<typeof marketplaceFeeConfigSchema>

export type CarrierCostUnavailable = {
  kind: "carrier-cost-unavailable"
  reason: "missing-weight" | "unsupported-weight"
  weightKg: number | null
}

/** Resolve one parcel's carrier price without throwing or inventing a zero.
 * The typed failure lets read paths preserve every independently known amount. */
export function resolveCarrierCostForWeight(
  tiers: readonly ShippingTier[],
  weightKg: number | null
): Result<number, CarrierCostUnavailable> {
  if (weightKg === null || weightKg <= 0) {
    return err({ kind: "carrier-cost-unavailable", reason: "missing-weight", weightKg })
  }
  for (const tier of tiers) {
    if ("upToKg" in tier && weightKg <= tier.upToKg) {
      return ok(tier.price)
    }
    if ("underKg" in tier && weightKg < tier.underKg) {
      return ok(tier.price)
    }
  }
  const perKg = tiers.find((tier): tier is { perKg: number } => "perKg" in tier)
  return perKg
    ? ok(perKg.perKg * weightKg)
    : err({ kind: "carrier-cost-unavailable", reason: "unsupported-weight", weightKg })
}

/** Resolve one parcel's carrier price. Missing/non-positive weight and weights
 * beyond a closed tier table stay unknown; callers decide whether that means a
 * null margin or a fail-closed rebuild. */
export function carrierCostForWeight(
  tiers: readonly ShippingTier[],
  weightKg: number | null
): number | null {
  const result = resolveCarrierCostForWeight(tiers, weightKg)
  return result.ok ? result.value : null
}
